import React, {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = Math.max(bytes, 0);
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function normalizeAccept(accept) {
  if (!accept) return '';
  return Array.isArray(accept) ? accept.join(',') : accept;
}

function getFileExt(name) {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

function fileMatchesAccept(file, acceptStr) {
  if (!acceptStr) return true;
  const tokens = acceptStr.split(',').map(t => t.trim()).filter(Boolean);
  if (tokens.length === 0) return true;

  const mime = (file.type || '').toLowerCase();
  const ext = getFileExt(file.name);

  for (const token of tokens) {
    if (!token) continue;
    const t = token.toLowerCase();

    if (t.startsWith('.')) {
      if (ext === t) return true;
      continue;
    }

    if (t.endsWith('/*')) {
      const prefix = t.slice(0, -1); 
      if (mime.startsWith(prefix)) return true;
      continue;
    }

    if (mime === t) return true;
  }

  return false;
}

function dedupeFiles(files) {
  const seen = new Set();
  const out = [];
  for (const f of files) {
    const key = `${f.name}__${f.size}__${f.type}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(f);
    }
  }
  return out;
}

const VARIANTS = {
  outline:
    'bg-white border border-gray-300 focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/20',
  filled:
    'bg-gray-50 border border-gray-300 focus-within:bg-white focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/20',
  unstyled:
    'bg-transparent border-transparent focus-within:border-transparent focus-within:ring-0',
};

const SIZE = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
};

const DROPZONE_SIZE = {
  sm: 'py-6',
  md: 'py-10',
  lg: 'py-14',
};

const BASE_BOX =
  'w-full rounded-md transition-colors focus:outline-none';
const DISABLED =
  'disabled:opacity-60 disabled:cursor-not-allowed';
const DROP_BASE =
  'relative flex flex-col items-center justify-center border-2 border-dashed text-center cursor-pointer';

const FileUploadInput = forwardRef(function FileUploadInput(
  {
    label,
    helperText,
    error,
    required = false,
    id: idProp,
    name,
    value,                 
    defaultFiles = [],     
    onChange,              
    onFilesChange,         
    onError,               
    multiple = false,
    accept,                
    capture,               
    directory = false,     
    mergeStrategy,         
    deduplicate = true,
    clearable = true,
    maxFiles,
    maxSizeBytes,          
    maxTotalSizeBytes,     
    mode = 'dropzone',     
    variant = 'outline',
    size = 'md',
    fullWidth = true,
    compact = false,       
    showPreview = false,   
    placeholder = 'Drag & drop files here, or click to browse',
    buttonLabel = 'Choose files',
    className,
    containerClassName,
    inputClassName,
    dropzoneClassName,
    listClassName,
    style,
    disabled = false,
    readOnly = false,
    loading = false,
    ...rest
  },
  externalRef
) {
  const reactId = useId();
  const id = idProp || `${name || 'file-upload'}-${reactId}`;
  const fileInputRef = useRef(null);
  const isControlled = value !== undefined;

  const [internalFiles, setInternalFiles] = useState(defaultFiles);
  const files = isControlled ? (value || []) : internalFiles;

  const [isDragging, setIsDragging] = useState(false);
  const [localErrors, setLocalErrors] = useState([]);

  const acceptAttr = normalizeAccept(accept);
  const effectiveMerge = mergeStrategy || (multiple ? 'append' : 'replace');

  const previews = useMemo(() => {
    if (!showPreview) return [];
    return files
      .map((f) => {
        const isImage = f.type.startsWith('image/');
        return {
          file: f,
          isImage,
          url: isImage ? URL.createObjectURL(f) : null,
        };
      });
  }, [files, showPreview]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (p.url) URL.revokeObjectURL(p.url);
      });
    };
  }, [previews]);

  const describedByIds = [];
  if (helperText) describedByIds.push(`${id}-help`);
  if (error || localErrors.length) describedByIds.push(`${id}-error`);

  const emitChange = (evt, nextFiles, errs = []) => {
    if (!isControlled) setInternalFiles(nextFiles);
    if (onChange) onChange(evt, nextFiles);
    if (onFilesChange) onFilesChange(nextFiles);
    if (errs.length) {
      setLocalErrors(errs);
      if (onError) onError(errs);
    } else {
      setLocalErrors([]);
    }
  };

  const currentTotalSize = useMemo(
    () => files.reduce((sum, f) => sum + (f?.size || 0), 0),
    [files]
  );

  const addFiles = (incoming, evt) => {
    let inc = Array.from(incoming || []).filter(Boolean);

    const acceptErrors = [];
    if (acceptAttr) {
      const before = inc.length;
      inc = inc.filter((f) => fileMatchesAccept(f, acceptAttr));
      if (inc.length !== before) {
        acceptErrors.push('Some files were rejected due to type restrictions.');
      }
    }

    const sizeErrors = [];
    if (Number.isFinite(maxSizeBytes)) {
      const before = inc.length;
      inc = inc.filter((f) => f.size <= maxSizeBytes);
      if (inc.length !== before) {
        sizeErrors.push(
          `Some files exceeded the per-file size limit (${formatBytes(maxSizeBytes)}).`
        );
      }
    }

    let next = [];
    if (effectiveMerge === 'replace') {
      next = inc;
    } else {
      next = [...files, ...inc];
    }

    if (deduplicate) next = dedupeFiles(next);

    const countErrors = [];
    if (Number.isFinite(maxFiles) && next.length > maxFiles) {
      countErrors.push(`Only ${maxFiles} file(s) allowed.`);
      next = next.slice(0, maxFiles);
    }

    const totalErrors = [];
    if (Number.isFinite(maxTotalSizeBytes)) {
      const total = next.reduce((s, f) => s + (f.size || 0), 0);
      if (total > maxTotalSizeBytes) {
        totalErrors.push(
          `Total size exceeds the limit (${formatBytes(maxTotalSizeBytes)}).`
        );
        let over = total;
        for (let i = next.length - 1; i >= 0 && over > maxTotalSizeBytes; i--) {
          const candidate = next[i];
          const isNew = inc.includes(candidate);
          if (isNew) {
            over -= candidate.size || 0;
            next.splice(i, 1);
          } else {
            break;
          }
        }
      }
    }

    const errs = [...acceptErrors, ...sizeErrors, ...countErrors, ...totalErrors];
    emitChange(evt, next, errs);
  };

  const removeAt = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    emitChange(undefined, next);
  };

  const clearAll = () => {
    emitChange(undefined, []);
  };

  const openDialog = () => {
    if (disabled || readOnly || loading) return;
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const fl = e?.target?.files;
    addFiles(fl, e);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || readOnly || loading) return;
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || readOnly || loading) return;
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || readOnly || loading) return;
    const fl = e.dataTransfer?.files;
    addFiles(fl, undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyDown = (e) => {
    if (disabled || readOnly || loading) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDialog();
    }
  };

  const sizeClasses = SIZE[size];
  const variantClasses = VARIANTS[variant];
  const dropPadding = compact ? 'py-4' : DROPZONE_SIZE[size] || DROPZONE_SIZE.md;

  const dropHighlight = isDragging
    ? 'border-blue-600 bg-blue-50'
    : 'border-gray-300 hover:border-blue-500';

  const disabledClasses = disabled || readOnly || loading ? 'opacity-60 cursor-not-allowed' : '';

  const describedBy =
    describedByIds.length ? describedByIds.join(' ') : undefined;

  const triggerButtonBase =
    'inline-flex items-center justify-center rounded-md font-medium transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600 ' +
    DISABLED;

  return (
    <div
      className={cn(fullWidth ? 'w-full' : '', containerClassName, className)}
      style={style}
    >
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}

      <input
        id={id}
        ref={(node) => {
          fileInputRef.current = node;
          if (typeof externalRef === 'function') externalRef(node);
          else if (externalRef && 'current' in externalRef) externalRef.current = node;
        }}
        name={name}
        type="file"
        className={cn('sr-only', inputClassName)}
        multiple={multiple}
        accept={acceptAttr || undefined}
        capture={capture}
        onChange={handleInputChange}
        disabled={disabled || readOnly || loading}
        aria-describedby={describedBy}
        {...(directory ? { webkitdirectory: 'true' } : {})}
        {...rest}
      />

      {mode === 'dropzone' ? (
        <div
          role="button"
          tabIndex={disabled || readOnly ? -1 : 0}
          onClick={openDialog}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-disabled={disabled || readOnly || loading || undefined}
          className={cn(
            BASE_BOX,
            DROP_BASE,
            variantClasses,
            dropPadding,
            dropHighlight,
            sizeClasses,
            disabledClasses,
            dropzoneClassName
          )}
        >
          {!loading ? (
            <>
              <svg
                className="h-8 w-8 text-gray-400 mb-2"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M12 16V4m0 0l-4 4m4-4l4 4" strokeWidth="2" strokeLinecap="round" />
                <rect x="3" y="14" width="18" height="6" rx="2" strokeWidth="2" />
              </svg>
              <p className="text-gray-700">
                {placeholder}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {multiple ? 'You can select multiple files.' : 'Single file only.'}
              </p>
              {acceptAttr && (
                <p className="text-gray-400 text-xs mt-1">
                  Accepted: {acceptAttr}
                </p>
              )}
            </>
          ) : (
            <p className="text-gray-500">Uploading…</p>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'flex items-center gap-3',
            fullWidth ? 'justify-between' : 'justify-start'
          )}
        >
          <button
            type="button"
            onClick={openDialog}
            disabled={disabled || readOnly || loading}
            className={triggerButtonBase}
          >
            <span className="px-4 py-2">{buttonLabel}</span>
          </button>

          {acceptAttr && (
            <span className="text-xs text-gray-500">Accepted: {acceptAttr}</span>
          )}
        </div>
      )}

      {files.length > 0 && (
        <div className={cn('mt-3 space-y-2', listClassName)}>
          {showPreview ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {previews.map((p, idx) => (
                <div
                  key={`${p.file.name}-${idx}`}
                  className={cn(
                    'group relative rounded-md border border-gray-200 overflow-hidden bg-white'
                  )}
                >
                  {p.isImage && p.url ? (
                    <img
                      src={p.url}
                      alt={p.file.name}
                      className="h-28 w-full object-cover"
                    />
                  ) : (
                    <div className="h-28 flex items-center justify-center text-gray-400">
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M4 4h16v16H4z" strokeWidth="2" />
                        <path d="M8 12h8M8 8h8M8 16h5" strokeWidth="2" />
                      </svg>
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-800 truncate">
                      {p.file.name}
                    </p>
                    <p className="text-[11px] text-gray-500">{formatBytes(p.file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="absolute top-1 right-1 rounded bg-white/90 text-gray-700 hover:text-red-600 px-1.5 py-0.5 text-xs shadow-sm"
                    aria-label={`Remove ${p.file.name}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
              {files.map((f, idx) => (
                <li key={`${f.name}-${idx}`} className="flex items-center justify-between px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 truncate">{f.name}</p>
                    <p className="text-xs text-gray-500">{formatBytes(f.size)}{f.type ? ` • ${f.type}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      className="text-sm text-gray-500 hover:text-red-600"
                      aria-label={`Remove ${f.name}`}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {clearable && files.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={clearAll}
                className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-4"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {helperText && !error && localErrors.length === 0 && (
        <p id={`${id}-help`} className="mt-1 text-xs text-gray-500">
          {helperText}
        </p>
      )}

      {(error || localErrors.length > 0) && (
        <div id={`${id}-error`} className="mt-1 text-xs text-red-600 space-y-0.5">
          {typeof error === 'string' ? <p>{error}</p> : null}
          {localErrors.map((msg, i) => (
            <p key={i}>{msg}</p>
          ))}
        </div>
      )}
    </div>
  );
});

export default FileUploadInput;
